import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './StudentDashboard.css';
import { getApiUrl, API_ENDPOINTS, getAuthHeaders } from '../../../../config/api.config';
import { toggleEnvironment } from '../../../../utils/apiClient';

const StudentDashboard = () => {
  const [stats, setStats] = useState({});
  const [recentSubmissions, setRecentSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Fetch school details and update trial status
  const fetchSchoolDetails = async () => {
    try {
      const response = await axios.get(
        getApiUrl('/api/v1/stem@home/auth/school-details'),
        { headers: getAuthHeaders() }
      );
      
      if (response.data.success && response.data.data) {
        const data = response.data.data;
        
        // Update localStorage with correct account type
        const userData = localStorage.getItem('user');
        if (userData) {
          const user = JSON.parse(userData);
          if (user && (user.accountType === 'trial') !== data.isTrial) {
            // Update the accountType to match what the server says
            user.accountType = data.isTrial ? 'trial' : 'paid';
            localStorage.setItem('user', JSON.stringify(user));
          }
        }
      }
    } catch (err) {
      console.error('Error fetching school details:', err);
    }
  };

  useEffect(() => {
    // First, fetch school details to ensure trial status is updated
    fetchSchoolDetails();
    
    const fetchStats = async () => {
      try {
        const userData = localStorage.getItem('user');
        if (!userData) {
          throw new Error('No user data found');
        }
        const user = JSON.parse(userData);

        // Fetch student stats
        try {
          const studentClassesResponse = await axios.get(
            getApiUrl(API_ENDPOINTS.STUDENT.CLASSES(user.userId)),
            { headers: getAuthHeaders() }
          );

          const studentSubmissionsResponse = await axios.get(
            getApiUrl(API_ENDPOINTS.STUDENT.SUBMISSIONS),
            { headers: getAuthHeaders() }
          );

          const submissionsData = studentSubmissionsResponse.data.data || [];
          const classesData = studentClassesResponse.data.classes || [];
          
          setStats({
            activeClasses: classesData.length,
            totalSubmissions: submissionsData.length || 0
          });

          if (Array.isArray(submissionsData) && submissionsData.length > 0) {
            const recentOnes = submissionsData
              .sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt))
              .slice(0, 3)
              .map(submission => ({
                _id: submission._id,
                submittedAt: submission.submittedAt,
                classId: submission.classId,
                totalScore: submission.totalScore,
                submissions: submission.submissions
              }));
            setRecentSubmissions(recentOnes);
          }
        } catch (err) {
          console.error('Error fetching student stats:', err);
          setStats({
            activeClasses: 0,
            totalSubmissions: 0
          });
          setRecentSubmissions([]);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching user data:', err);
        setStats({
          activeClasses: 0,
          totalSubmissions: 0
        });
        setRecentSubmissions([]);
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return <div className="loading">Loading dashboard data...</div>;
  }

  return (
    <div className="dashboard-container">
      <div className="page-content">
        <div className="content-header">
          {/* Removed the first Dashboard title */}
        </div>
        
        <div className="dashboard-stats-container">
          <div className="dashboard-header" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <h1 style={{ margin: '0 auto', textAlign: 'center', width: '100%' }}>Dashboard</h1>
          </div>

          <div className="stats-grid">
            <div 
              className="stat-card" 
              onClick={() => navigate('/my-classes')}
              style={{ cursor: 'pointer' }}
            >
              <h2>Active Classes</h2>
              <div className="number">{stats.activeClasses}</div>
            </div>
            <div 
              className="stat-card"
              onClick={() => navigate('/my-submissions')}
              style={{ cursor: 'pointer' }}
            >
              <h2>Total Submissions</h2>
              <div className="number">{stats.totalSubmissions}</div>
            </div>
          </div>

          <div className="recent-submissions">
            <div className="section-header">
              <h2>Recent Submissions</h2>
              <Link to="/my-submissions" className="view-all-link">View All</Link>
            </div>

            {recentSubmissions.length === 0 ? (
              <div key="no-submissions" className="no-submissions">No recent submissions</div>
            ) : (
              <div className="submissions-list">
                {recentSubmissions.map((submission) => (
                  <div 
                    key={submission._id} 
                    className="submission-card"
                    onClick={() => {
                      navigate('/my-submissions');
                      setTimeout(() => {
                        const event = new CustomEvent('viewSubmissionDetails', { 
                          detail: { submissionId: submission._id }
                        });
                        window.dispatchEvent(event);
                      }, 100);
                    }}
                  >
                    <div className="submission-header">
                      <div className="class-name">
                        Class: {submission.classId?.name || 'Unknown Class'}
                      </div>
                      <div className="submission-date">
                        {new Date(submission.submittedAt).toLocaleDateString('en-US', {
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric'
                        })} ({new Date(submission.submittedAt).toLocaleTimeString('en-US', {
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: false
                        })})
                      </div>
                    </div>
                    <div className="submission-score">
                      Score: {submission.totalScore || '0'}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentDashboard; 